import { useMemo } from "react";
import { LDProvider } from "launchdarkly-react-client-sdk";
import { createLDContext } from "../utils/launchDarklyUtils";
import { defaultLDFlags } from "../constants/LDConstants";

/**
 * ConditionalLDProvider - Initializes LaunchDarkly ONLY after domainId exists
 *
 * This prevents anonymous user creation by ensuring LD is initialized with
 * a proper context from the start. No anonymous users are ever created.
 *
 * GENERIC SOLUTION: Uses synchronous LDProvider instead of async to avoid
 * state changes that trigger re-renders. The LD SDK will initialize in the
 * background without causing React component re-renders.
 *
 * @param {Object} props
 * @param {Object} props.domain - Domain data containing domainId
 * @param {React.ReactNode} props.children - Child components
 */
const ConditionalLDProvider = ({ domain, children }) => {
    // Memoize the config to prevent re-initialization on re-renders
    const ldConfig = useMemo(() => {
        if (!domain?.domainId) {
            return null;
        }

        return {
            clientSideID: process.env.REACT_APP_LAUNCHDARKLY_CLIENTID,
            context: createLDContext(domain),
            flags: defaultLDFlags, // These are used as initial values before LD SDK fetches real flags
            options: {
                bootstrap: "localStorage",
            },
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domain?.domainId]); // Only re-create when domainId changes, not when domain object reference changes

    // If no domainId yet, render children without LD
    if (!ldConfig) {
        return <>{children}</>;
    }

    // Use synchronous LDProvider which doesn't cause re-renders
    // LD will initialize in background without triggering React updates
    return (
        <LDProvider clientSideID={ldConfig.clientSideID} context={ldConfig.context} flags={ldConfig.flags} options={ldConfig.options}>
            {children}
        </LDProvider>
    );
};

export default ConditionalLDProvider;
